import { type ChangeEvent, type FormEvent, type JSX, useEffect, useRef, useState } from 'react';

import {
  EloBanner,
  EloButton,
  EloIcon,
  EloProgressCircle,
  EloTextLink,
  RadioGroup,
  type EloIconType,
} from '@plurall/elo-v3';
import { useMutation } from '@tanstack/react-query';

import { submitUserSatisfaction, type UserSatisfactionPayload } from '@/api/user/userSatisfactionApi';
import { useAuthentication } from '@/hooks/queries/authentication/useAuthentication';
import { resolvePath } from '@/lib/formatters/resolveEnvUrl';

import {
  FeedbackFloaterActionsFactory,
  FeedbackFloaterCounterFactory,
  FeedbackFloaterFieldFactory,
  FeedbackFloaterFormFactory,
  FeedbackFloaterHelperFactory,
  FeedbackFloaterLoadingFactory,
  FeedbackFloaterPanelFactory,
  FeedbackFloaterRootFactory,
  FeedbackFloaterScoreFactory,
  FeedbackFloaterScoreIconFactory,
  FeedbackFloaterScoreLabelFactory,
  FeedbackFloaterScoresFactory,
  FeedbackFloaterSuccessFactory,
  FeedbackFloaterTextFactory,
  FeedbackFloaterTextareaFactory,
  FeedbackFloaterTitleFactory,
  FeedbackFloaterTriggerFactory,
} from './FeedbackFloater.factory';
import { removeEmojis } from './FeedbackFloater.utils';

import type { FeedbackFloaterProps, FeedbackScore } from './FeedbackFloater.types';

const MAX_COMMENT_LENGTH = 200;
const CONTACT_URL = resolvePath('usuario/contato');
const FULL_SURVEY_URL = resolvePath('avaliacao/pesquisa-de-satisfacao');
const SCORES: Array<{ value: FeedbackScore; label: string; shortLabel: string; icon: EloIconType }> = [
  { value: 1, label: '1 — Péssimo', shortLabel: 'péssimo', icon: 'face-hate-circle-fill' },
  { value: 2, label: '2 — Ruim', shortLabel: 'ruim', icon: 'face-dislike-circle-fill' },
  { value: 3, label: '3 — Mediano', shortLabel: 'mediano', icon: 'face-neutral-circle-fill' },
  { value: 4, label: '4 — Bom', shortLabel: 'bom', icon: 'face-like-circle-fill' },
  { value: 5, label: '5 — Ótimo', shortLabel: 'ótimo', icon: 'face-love-circle-fill' },
];

export function FeedbackFloater({ panelId = 'feedback-floater-panel' }: FeedbackFloaterProps): JSX.Element | null {
  const { data: user } = useAuthentication();
  const canAnswer = user?.indRespondePesquisaSatisfacao === true;
  const isProfessor = user?.perfil === 'PROFESSOR';
  const [isOpen, setIsOpen] = useState(false);
  const [isPanelMounted, setIsPanelMounted] = useState(false);
  const [score, setScore] = useState<FeedbackScore | null>(null);
  const [comment, setComment] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const hasHandledAutoOpenRef = useRef(false);
  const successRef = useRef<HTMLHeadingElement>(null);

  const mutation = useMutation({
    mutationFn: submitUserSatisfaction,
    onSuccess: () => setHasSubmitted(true),
  });

  useEffect(() => {
    if (hasHandledAutoOpenRef.current || !canAnswer) return;

    hasHandledAutoOpenRef.current = true;
    if (isProfessor) {
      setIsPanelMounted(true);
      setIsOpen(true);
    }
  }, [canAnswer, isProfessor]);

  useEffect(() => {
    if (hasSubmitted) successRef.current?.focus();
  }, [hasSubmitted]);

  if (!canAnswer) return null;

  const isCommentRequired = score !== null && score <= 3;
  const isCommentVisible = score !== null && !hasSubmitted;
  const isValid = score !== null && (!isCommentRequired || comment.trim().length > 0);

  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setComment(removeEmojis(event.target.value).slice(0, MAX_COMMENT_LENGTH));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!isValid || score === null || mutation.isPending) return;

    const trimmedComment = comment.trim();
    const payload: UserSatisfactionPayload = {
      nota: score,
      ...(trimmedComment.length > 0 ? { comentario: trimmedComment } : {}),
    };
    mutation.mutate(payload);
  };

  const handleOpenChange = (nextIsOpen: boolean): void => {
    if (nextIsOpen) setIsPanelMounted(true);
    setIsOpen(nextIsOpen);
  };

  return (
    <FeedbackFloaterRootFactory
      aria-label='Pesquisa de satisfação'
      data-open={String(isOpen)}
      data-comment-visible={String(isCommentVisible)}>
      {isPanelMounted ? (
        <FeedbackFloaterPanelFactory
          id={panelId}
          data-open={String(isOpen)}
          data-comment-visible={String(isCommentVisible)}
          aria-hidden={!isOpen}
          aria-labelledby={`${panelId}-title`}
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget && !isOpen) setIsPanelMounted(false);
          }}>
          {hasSubmitted ? (
            <FeedbackFloaterSuccessFactory>
              <FeedbackFloaterTitleFactory
                ref={successRef}
                id={`${panelId}-title`}
                tabIndex={-1}>
                Obrigado pela sua avaliação!
              </FeedbackFloaterTitleFactory>
              <FeedbackFloaterTextFactory>
                Que tal aproveitar para responder a uma{' '}
                <EloTextLink
                  variant='primary'
                  use='inline'
                  href={FULL_SURVEY_URL}>
                  pesquisa mais completa
                </EloTextLink>
                ?
              </FeedbackFloaterTextFactory>
              <FeedbackFloaterTextFactory>
                Precisa de ajuda?{' '}
                <EloTextLink
                  variant='primary'
                  use='inline'
                  href={CONTACT_URL}>
                  Entre em contato
                </EloTextLink>
              </FeedbackFloaterTextFactory>
              <EloButton.Brand
                type='button'
                onClick={() => handleOpenChange(false)}>
                Fechar
              </EloButton.Brand>
            </FeedbackFloaterSuccessFactory>
          ) : (
            <FeedbackFloaterFormFactory
              data-has-selection={String(score !== null)}
              onSubmit={handleSubmit}>
              <FeedbackFloaterTitleFactory id={`${panelId}-title`}>
                {isProfessor
                  ? 'O que você está achando de realizar correções para a RED1000?'
                  : 'O que você está achando da plataforma do Redação Nota 1000?'}
              </FeedbackFloaterTitleFactory>

              <FeedbackFloaterScoresFactory
                aria-label='Sua nota'
                data-has-selection={String(score !== null)}
                value={score === null ? null : String(score)}
                disabled={mutation.isPending}
                onValueChange={({ value }) => {
                  setScore(Number(value) as FeedbackScore);
                  mutation.reset();
                }}>
                {SCORES.map(({ value, label, shortLabel, icon }) => (
                  <FeedbackFloaterScoreFactory
                    key={value}
                    value={String(value)}
                    data-score={String(value)}
                    aria-label={label}>
                    <RadioGroup.ItemHiddenInput />
                    <FeedbackFloaterScoreLabelFactory aria-hidden='true'>{shortLabel}</FeedbackFloaterScoreLabelFactory>
                    <FeedbackFloaterScoreIconFactory
                      icon={icon}
                      aria-hidden='true'
                    />
                  </FeedbackFloaterScoreFactory>
                ))}
              </FeedbackFloaterScoresFactory>

              {score !== null ? (
                <FeedbackFloaterFieldFactory>
                  <FeedbackFloaterTextareaFactory
                    id={`${panelId}-comment`}
                    name='comentario'
                    value={comment}
                    maxLength={MAX_COMMENT_LENGTH}
                    required={isCommentRequired}
                    disabled={mutation.isPending}
                    placeholder='Deixe o seu comentário'
                    aria-label={`Comentário${isCommentRequired ? ' (obrigatório)' : ' (opcional)'}`}
                    aria-describedby={
                      isCommentRequired
                        ? `${panelId}-comment-help ${panelId}-comment-count`
                        : `${panelId}-comment-count`
                    }
                    onChange={handleCommentChange}
                  />
                  {isCommentRequired ? (
                    <FeedbackFloaterHelperFactory id={`${panelId}-comment-help`}>
                      Uma descrição é necessária para notas iguais ou inferiores a 3
                    </FeedbackFloaterHelperFactory>
                  ) : null}
                  <FeedbackFloaterCounterFactory
                    id={`${panelId}-comment-count`}
                    aria-live='polite'>
                    {comment.length}/{MAX_COMMENT_LENGTH}
                  </FeedbackFloaterCounterFactory>
                </FeedbackFloaterFieldFactory>
              ) : null}

              <FeedbackFloaterTextFactory>
                Precisa de ajuda?{' '}
                <EloTextLink
                  variant='primary'
                  use='inline'
                  href={CONTACT_URL}>
                  Entre em contato
                </EloTextLink>
              </FeedbackFloaterTextFactory>

              {mutation.isError ? (
                <EloBanner
                  status='error'
                  variant='inline'
                  layout='multipleLines'
                  title='Não foi possível enviar sua avaliação'
                  content='Tente novamente em instantes.'
                />
              ) : null}

              <FeedbackFloaterActionsFactory>
                <EloButton.Neutral
                  type='button'
                  variant='outline'
                  disabled={mutation.isPending}
                  onClick={() => handleOpenChange(false)}>
                  Cancelar
                </EloButton.Neutral>
                <EloButton.Brand
                  type='submit'
                  disabled={!isValid || mutation.isPending}
                  aria-busy={mutation.isPending}>
                  {mutation.isPending ? (
                    <FeedbackFloaterLoadingFactory>
                      <EloProgressCircle
                        size='x-small'
                        type='standalone'
                        aria-label='Enviando avaliação'
                      />
                      Enviando
                    </FeedbackFloaterLoadingFactory>
                  ) : (
                    'Enviar'
                  )}
                </EloButton.Brand>
              </FeedbackFloaterActionsFactory>
            </FeedbackFloaterFormFactory>
          )}
        </FeedbackFloaterPanelFactory>
      ) : null}
      <FeedbackFloaterTriggerFactory
        type='button'
        data-open={String(isOpen)}
        aria-label={isOpen ? 'Fechar feedback' : 'Abrir feedback'}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => handleOpenChange(!isOpen)}>
        <EloIcon
          icon={isOpen ? 'cancel' : 'star-fill'}
          aria-hidden='true'
        />
        Feedback
      </FeedbackFloaterTriggerFactory>
    </FeedbackFloaterRootFactory>
  );
}
